import { getSupabase } from "./supabase";
import { latestRates } from "./rateInputs.mjs";
import type { RateClear, RateRow } from "./rateInputs.d.mts";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-585: read, append and clear operator-entered rates.
 *
 * An edit is an INSERT that supersedes, never an UPDATE: the newest row per
 * key is the value in force. A rate is taken off with the
 * tenant_rate_input_clear function (migration 0100), which removes every
 * stored row of that key; the table has no update or delete policy, so there
 * is no other way to change or remove a row. The database decides who may
 * write or clear which key (owner, viewer and staff for the client keys, staff
 * for the rest), and every insert and every clear is kept in report_edit_log.
 *
 * Settings > Inputs (2026-10-01) reads and writes through this same file, so
 * a value shown there and on its report is one stored row. Two things it
 * needs that the reports did not: the writer (created_by, for "who last
 * changed this") and dated entries (an Executive input per month:
 * effective_date on the write, tenant_rate_input_clear_dated to take one
 * month's entry off, migration 0105).
 */

const COLUMNS =
  "id,scope,scope_key,rate_key,rate_value,rate_unit,effective_date,note,created_by,created_at";

export async function fetchRateInputs(tenantId: string): Promise<Map<string, RateRow>> {
  const supabase = getSupabase();
  const { data, error } = await scopeTenant(
    supabase.from("tenant_rate_input").select(COLUMNS),
    tenantId,
  );
  // Fail quiet, like the flag map: an unreadable rate table must degrade to an
  // empty panel, never a broken page.
  if (error || !Array.isArray(data)) return new Map();
  return latestRates(data as RateRow[]);
}

/**
 * Every stored row of the named rate keys for the tenant, uncollapsed, for a
 * reader that keeps more than one entry per key (an Executive input per
 * month). Unlike fetchRateInputs this THROWS on a failed read: Settings >
 * Inputs must say it could not load rather than show an input as empty.
 */
export async function fetchRateInputRows(
  tenantId: string,
  rateKeys: readonly string[],
): Promise<RateRow[]> {
  if (rateKeys.length === 0) return [];
  const { data, error } = await scopeTenant(
    getSupabase().from("tenant_rate_input").select(COLUMNS),
    tenantId,
  )
    .in("rate_key", [...rateKeys])
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as RateRow[];
}

export interface RateWrite {
  scope: string;
  scopeKey: string;
  rateKey: string;
  value: number;
  unit: string;
  note?: string;
  /** YYYY-MM-DD the value is for (a dated entry). Absent: the table's default, today. */
  effectiveDate?: string;
}

export async function appendRateInputs(
  tenantId: string,
  writes: readonly RateWrite[],
): Promise<{ ok: boolean; error: string | null }> {
  if (writes.length === 0) return { ok: true, error: null };
  const supabase = getSupabase();
  const rows = writes.map((w) => ({
    tenant_id: tenantId,
    scope: w.scope,
    scope_key: w.scopeKey,
    rate_key: w.rateKey,
    rate_value: w.value,
    rate_unit: w.unit,
    note: w.note ?? null,
    ...(w.effectiveDate ? { effective_date: w.effectiveDate } : {}),
  }));
  // tenant_id is set explicitly on every row above; RLS also checks it, and
  // `created_by` defaults to auth.uid() so the insert policy can bind it.
  const { error } = await supabase.from("tenant_rate_input").insert(rows);
  return { ok: !error, error: error ? error.message : null };
}

export type { RateClear };

export interface RateClearResult {
  ok: boolean;
  /** The first failure's message, when any clear failed. */
  error: string | null;
  /** The clears that went through, so the caller shows exactly what happened. */
  cleared: RateClear[];
}

/**
 * Take saved rates off: one tenant_rate_input_clear call per
 * {scope, scopeKey, rateKey}, each removing every stored row of that key for
 * the tenant (migration 0100; refused with 42501 for a key or a tenant the
 * session may not clear). Readers then see no rate for the key.
 */
export async function clearRateInputs(
  tenantId: string,
  clears: readonly RateClear[],
): Promise<RateClearResult> {
  if (clears.length === 0) return { ok: true, error: null, cleared: [] };
  const supabase = getSupabase();
  const errors = await Promise.all(
    clears.map(async (c): Promise<string | null> => {
      try {
        const { error } = await supabase.rpc("tenant_rate_input_clear", {
          p_tenant: tenantId,
          p_scope: c.scope,
          p_scope_key: c.scopeKey,
          p_rate_key: c.rateKey,
        });
        return error ? error.message : null;
      } catch (e) {
        return e instanceof Error ? e.message : String(e);
      }
    }),
  );
  const error = errors.find((e) => e !== null) ?? null;
  return { ok: error === null, error, cleared: clears.filter((_, i) => errors[i] === null) };
}

/** One dated entry to remove: the stored rows of a key for one effective date. */
export interface DatedRateClear extends RateClear {
  /** YYYY-MM-DD */
  effectiveDate: string;
}

/**
 * Take dated entries off: one tenant_rate_input_clear_dated call per
 * {scope, scopeKey, rateKey, effectiveDate} (migration 0105), each removing
 * the stored rows of that key for that date only, so clearing one month of a
 * monthly input leaves every other month in place. Same refusals as
 * clearRateInputs (42501 for a key or a tenant the session may not clear).
 */
export async function clearDatedRateInputs<T extends DatedRateClear>(
  tenantId: string,
  clears: readonly T[],
): Promise<{ ok: boolean; error: string | null; cleared: T[] }> {
  if (clears.length === 0) return { ok: true, error: null, cleared: [] };
  const supabase = getSupabase();
  const errors = await Promise.all(
    clears.map(async (c): Promise<string | null> => {
      try {
        const { error } = await supabase.rpc("tenant_rate_input_clear_dated", {
          p_tenant: tenantId,
          p_scope: c.scope,
          p_scope_key: c.scopeKey,
          p_rate_key: c.rateKey,
          p_effective_date: c.effectiveDate,
        });
        return error ? error.message : null;
      } catch (e) {
        return e instanceof Error ? e.message : String(e);
      }
    }),
  );
  const error = errors.find((e) => e !== null) ?? null;
  return { ok: error === null, error, cleared: clears.filter((_, i) => errors[i] === null) };
}

/**
 * Whether the tenant's own users may write a rate key: the database's own
 * answer (tenant_rate_input_client_key, migration 0100, widened by 0105), so
 * a control is never offered for a key the insert policy would refuse. False
 * on any failure.
 */
export async function isClientRateKey(scope: string, rateKey: string): Promise<boolean> {
  try {
    const { data, error } = await getSupabase().rpc("tenant_rate_input_client_key", {
      p_scope: scope,
      p_rate_key: rateKey,
    });
    return !error && data === true;
  } catch {
    return false;
  }
}
